import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Table, Input, Select, Button, Space, Form, Card } from "antd";
import { SearchOutlined, ReloadOutlined } from "@ant-design/icons";
import type { TableProps, TablePaginationConfig } from "antd";
import type { ColumnType } from "antd/es/table";

const { Option } = Select;

// 搜索字段配置
export interface SearchFieldConfig {
  /** 字段名 */
  key: string;
  /** 字段标签 */
  label: string;
  /** 搜索类型 */
  type: 'input' | 'select';
  /** 选择器选项（当type为select时使用） */
  options?: Array<{ label: string; value: string | number }>;
  /** 占位符 */
  placeholder?: string;
  /** 默认值 */
  defaultValue?: unknown;
}

// 列配置扩展
export interface AdvancedColumnType<T = Record<string, unknown>> extends ColumnType<T> {
  /** 是否支持排序 */
  sortable?: boolean;
  /** 自定义渲染函数 */
  render?: (value: unknown, record: T, index: number) => React.ReactNode;
}

// 请求参数
export interface RequestParams {
  /** 页码 */
  page: number;
  /** 每页大小 */
  pageSize: number;
  /** 排序字段 */
  sortField?: string;
  /** 排序方向 */
  sortOrder?: 'ascend' | 'descend';
  /** 搜索参数 */
  searchParams?: Record<string, unknown>;
}

// 请求响应
export interface RequestResponse<T = Record<string, unknown>> {
  /** 数据列表 */
  data: T[];
  /** 总数 */
  total: number;
  /** 是否成功 */
  success: boolean;
  /** 错误信息 */
  message?: string;
}

// 组件属性
export interface AdvancedTableProps<T = Record<string, unknown>> extends Omit<TableProps<T>, 'dataSource' | 'loading' | 'pagination'> {
  /** 列配置 */
  columns: AdvancedColumnType<T>[];
  /** 异步请求函数 */
  request?: (params: RequestParams) => Promise<RequestResponse<T>>;
  /** 搜索字段配置 */
  searchFields?: SearchFieldConfig[];
  /** 是否显示搜索区域 */
  showSearch?: boolean;
  /** 是否显示刷新按钮 */
  showRefresh?: boolean;
  /** 默认页面大小 */
  defaultPageSize?: number;
  /** 页面大小选项 */
  pageSizeOptions?: string[];
  /** 是否自动加载 */
  autoLoad?: boolean;
  /** 请求参数（外部控制） */
  externalParams?: Record<string, unknown>;
}

type DocgenInfo = {
  displayName: string;
  description: string;
  props: Record<string, {
    name: string;
    required: boolean;
    description: string;
    type: { name: string; raw: string };
    defaultValue?: { value: string };
  }>;
};

type ComponentWithDocgen = React.FC<AdvancedTableProps> & { __docgenInfo?: DocgenInfo };

/** 高阶表格组件，支持异步请求、搜索、排序等功能 */
export const AdvancedTable: React.FC<AdvancedTableProps> = ({
  columns,
  request,
  searchFields = [],
  showSearch = true,
  showRefresh = true,
  defaultPageSize = 10,
  pageSizeOptions = ['10', '20', '50', '100'],
  autoLoad = true,
  externalParams = {},
  ...tableProps
}) => {
  // 状态管理
  const [dataSource, setDataSource] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: defaultPageSize,
    total: 0,
  });
  const [searchParams, setSearchParams] = useState<Record<string, unknown>>({});
  const [sortInfo, setSortInfo] = useState<{
    field?: string;
    order?: 'ascend' | 'descend';
  }>({});

  // 搜索表单
  const [form] = Form.useForm();
  
  // 使用 ref 来避免依赖问题
  const paginationRef = useRef(pagination);
  paginationRef.current = pagination;
  // 使用 ref 来避免 externalParams 的依赖问题
  const externalParamsRef = useRef(externalParams);
  externalParamsRef.current = externalParams;

  // 使用 ref 来跟踪是否已经初始化
  const initializedRef = useRef(false);

  // 处理列配置
  const processedColumns = useMemo(() => {
    return columns.map((col) => {
      const { sortable, render, ...restCol } = col;
      
      return {
        ...restCol,
        sorter: sortable ? true : undefined,
        render: render || ((value: unknown) => value as React.ReactNode),
      };
    });
  }, [columns]);

  // 加载数据
  const loadData = useCallback(async (params: Partial<RequestParams> = {}) => {
    if (!request) return;

    setLoading(true);
    try {
      const requestParams: RequestParams = {
        page: paginationRef.current.current,
        pageSize: paginationRef.current.pageSize,
        sortField: sortInfo.field,
        sortOrder: sortInfo.order,
        searchParams: { ...searchParams, ...externalParamsRef.current },
        ...params,
      };

      const response = await request(requestParams);
      
      if (response.success) {
        setDataSource(response.data);
        setPagination(prev => ({
          ...prev,
          total: response.total,
          current: requestParams.page || prev.current,
        }));
      }
    } catch (error) {
      console.error('加载数据失败:', error);
    } finally {
      setLoading(false);
    }
  }, [request, sortInfo, searchParams]);

  // 搜索处理
  const handleSearch = useCallback((values: Record<string, unknown>) => {
    setSearchParams(values);
    setPagination(prev => ({ ...prev, current: 1 }));
  }, []);

  // 重置搜索
  const handleReset = useCallback(() => {
    form.resetFields();
    setSearchParams({});
    setPagination(prev => ({ ...prev, current: 1 }));
  }, [form]);

  // 刷新数据
  const handleRefresh = useCallback(() => {
    loadData();
  }, [loadData]);

  // 分页变化
  const handleTableChange = useCallback((pagination: TablePaginationConfig, _filters: unknown, sorter: unknown) => {
    const newPagination = {
      current: pagination.current || 1,
      pageSize: pagination.pageSize || defaultPageSize,
      total: pagination.total || 0,
    };
    
    const newSortInfo = {
      field: (sorter as { field?: string })?.field,
      order: (sorter as { order?: 'ascend' | 'descend' })?.order,
    };

    setPagination(newPagination);
    setSortInfo(newSortInfo);
  }, [defaultPageSize]);

  // 初始加载和外部参数变化时重新加载
  useEffect(() => {
    const hasExternalParams = Object.keys(externalParams).length > 0;

    // 如果是自动加载且未初始化，或者是外部参数变化
    if ((autoLoad && !initializedRef.current) || hasExternalParams) {
      loadData();
      initializedRef.current = true;
    }
  }, [autoLoad, externalParams, loadData]);

  return (
    <div>
      {/* 搜索区域 */}
      {showSearch && searchFields.length > 0 && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Form
            form={form}
            layout="inline"
            onFinish={handleSearch}
            initialValues={searchFields.reduce((acc, field) => {
              if (field.defaultValue !== undefined) {
                acc[field.key] = field.defaultValue;
              }
              return acc;
            }, {} as Record<string, unknown>)}
          >
            {searchFields.map((field) => (
              <Form.Item key={field.key} name={field.key} label={field.label}>
                {field.type === 'input' ? (
                  <Input
                    placeholder={field.placeholder || `请输入${field.label}`}
                    style={{ width: 200 }}
                  />
                ) : (
                  <Select
                    placeholder={field.placeholder || `请选择${field.label}`}
                    style={{ width: 200 }}
                    allowClear
                  >
                    {field.options?.map((option) => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                )}
              </Form.Item>
            ))}
            
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                  查询
                </Button>
                <Button onClick={handleReset}>重置</Button>
                {showRefresh && (
                  <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
                    刷新
                  </Button>
                )}
              </Space>
            </Form.Item>
          </Form>
        </Card>
      )}

      {/* 表格 */}
      <Table
        {...tableProps}
        columns={processedColumns}
        dataSource={dataSource}
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
          pageSizeOptions,
        }}
        onChange={handleTableChange}
        scroll={{ x: 'max-content', y: '50vh' }}
      />
    </div>
  );
};

// 手动添加 docgen 信息
(AdvancedTable as ComponentWithDocgen).__docgenInfo = {
  displayName: "AdvancedTable",
  description: "高阶表格组件，支持异步请求、搜索、排序等功能",
  props: {
    columns: {
      name: "columns",
      required: true,
      description: "列配置",
      type: { name: "AdvancedColumnType[]", raw: "AdvancedColumnType<any>[]" }
    },
    request: {
      name: "request",
      required: false,
      description: "异步请求函数",
      type: { name: "function", raw: "(params: RequestParams) => Promise<RequestResponse>" }
    },
    searchFields: {
      name: "searchFields",
      required: false,
      description: "搜索字段配置",
      type: { name: "SearchFieldConfig[]", raw: "SearchFieldConfig[]" },
      defaultValue: { value: "[]" }
    },
    showSearch: {
      name: "showSearch",
      required: false,
      description: "是否显示搜索区域",
      type: { name: "boolean", raw: "boolean" },
      defaultValue: { value: "true" }
    },
    showRefresh: {
      name: "showRefresh",
      required: false,
      description: "是否显示刷新按钮",
      type: { name: "boolean", raw: "boolean" },
      defaultValue: { value: "true" }
    },
    defaultPageSize: {
      name: "defaultPageSize",
      required: false,
      description: "默认页面大小",
      type: { name: "number", raw: "number" },
      defaultValue: { value: "10" }
    },
    pageSizeOptions: {
      name: "pageSizeOptions",
      required: false,
      description: "页面大小选项",
      type: { name: "string[]", raw: "string[]" },
      defaultValue: { value: '["10", "20", "50", "100"]' }
    },
    autoLoad: {
      name: "autoLoad",
      required: false,
      description: "是否自动加载",
      type: { name: "boolean", raw: "boolean" },
      defaultValue: { value: "true" }
    },
    externalParams: {
      name: "externalParams",
      required: false,
      description: "外部请求参数",
      type: { name: "Record<string, any>", raw: "Record<string, any>" },
      defaultValue: { value: "{}" }
    }
  }
};
